<template>
  <div class="price-adjustment">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-neutral-800">Price Adjustment</h1>
      <p class="text-sm text-neutral-500 mt-1">
        Manage price adjustments for orders with discrepancies
      </p>
    </div>

    <div
      class="flex items-end justify-between gap-5 mb-6 mdmax:flex-col mdmax:items-start"
    >
      <div
        class="flex items-end gap-5 mdmax:flex-col mdmax:items-start mdmax:gap-3 mdmax:w-full"
      >
        <div class="w-60 mdmax:w-full">
          <label class="text-sm text-neutral-700 font-medium mb-1 inline-block"
            >Status</label
          >
          <CustomSelect
            :options="statusOptions"
            v-model="selectedStatus"
            :default="selectedStatus"
            placeholder="All Status"
            @change="handleFilterChange"
          />
        </div>
        <div class="w-60 mdmax:w-full">
          <label class="text-sm text-neutral-700 font-medium mb-1 inline-block"
            >Rentang Tanggal</label
          >
          <div class="date-range relative text-xs">
            <div class="absolute left-3 top-1/2 transform -translate-y-1/2 z-1">
              <Calendar color="neutral-700" />
            </div>
            <date-range-picker
              v-model="dateRange"
              :locale-data="{ format: 'dd/mm/yyyy', separator: ' - ' }"
              @update="handleDateRangeChange"
              :showDropdowns="false"
              :timePicker="false"
              :timePicker24Hour="false"
              :ranges="false"
            ></date-range-picker>
          </div>
        </div>
        <div class="w-40 mdmax:flex-1">
          <label class="text-sm text-neutral-700 font-medium mb-1 inline-block"
            >Sort</label
          >
          <CustomSelect
            :options="sortOptions"
            v-model="selectedSort"
            :default="selectedSort"
            @change="handleFilterChange"
          />
        </div>
      </div>
    </div>

    <div class="">
      <TableComponent :columns="columns" :list="priceAdjustmentList" compact>
        <template slot="table-row" slot-scope="{ entry, column }">
          <div v-if="column.field === 'order_id'">
            <p class="font-medium">{{ entry.order_id }}</p>
            <p class="text-xs text-neutral-500">ID: {{ entry.id }}</p>
          </div>
          <div v-else-if="column.field === 'old_details'">
            <p class="text-sm">
              <span class="font-medium">{{ entry.old_weight }} kg</span>
              <span class="text-neutral-500 mx-1">|</span>
              <span class="text-neutral-500">{{ entry.old_dimension }}</span>
            </p>
            <p class="text-sm font-semibold text-neutral-700 mt-1">
              {{ entry.old_price | currencyFormat }}
            </p>
          </div>
          <div v-else-if="column.field === 'new_details'">
            <p class="text-sm">
              <span class="font-medium">{{ entry.new_weight }} kg</span>
              <span class="text-neutral-500 mx-1">|</span>
              <span class="text-neutral-500">{{ entry.new_dimension }}</span>
            </p>
            <p class="text-sm font-semibold text-neutral-700 mt-1">
              {{ entry.new_price | currencyFormat }}
            </p>
          </div>
          <div v-else-if="column.field === 'difference'">
            <p class="text-sm font-bold text-red-600">
              +{{ (entry.new_price - entry.old_price) | currencyFormat }}
            </p>
          </div>
          <div v-else-if="column.field === 'status'">
            <span
              :class="getStatusClass(entry.status)"
              class="px-3 py-1 rounded-full text-xs font-medium inline-block"
            >
              {{ entry.status }}
            </span>
          </div>
          <div v-else-if="column.field === 'created_at'">
            {{ entry.created_at | formatDate }}
          </div>
          <div v-else-if="column.field === 'action'">
            <div
              v-if="entry.status === 'Payment Under Verification'"
              class="flex gap-2"
            >
              <button
                @click="handleCompleteClick(entry.order_id)"
                class="px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                Complete
              </button>
              <button
                @click="handleRejectClick(entry.order_id)"
                class="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Reject
              </button>
            </div>
            <span v-else class="text-xs text-neutral-400">-</span>
          </div>
          <span v-else>{{ entry[column.field] || "-" }}</span>
        </template>
      </TableComponent>

      <template v-if="priceAdjustmentList?.length > 0">
        <div class="text-neutral-500 font-medium mt-2 text-sm" v-if="!isMobile">
          Menampilkan {{ start }} - {{ end }} dari {{ totalRecords }}
        </div>
        <div
          class="mt-8 flex justify-end items-center mb-6"
          v-if="priceAdjustmentList && priceAdjustmentList.length > 0"
        >
          <pagination
            :currentPage="currentPage"
            :totalCount="totalRecords"
            :pageSize="pageSize"
            @onPageChange="onPageChange"
          />
        </div>
      </template>

      <div v-else-if="!isLoading" class="text-center py-12 text-neutral-500">
        <p class="text-lg">No price adjustments found</p>
      </div>
    </div>

    <Modal
      :modalVisible="visibleConfirmModal"
      @close="toggleConfirmModal()"
      width="600px"
      id="confirmation-modal"
    >
      <template slot="modal-content">
        <div class="flex items-center justify-start gap-6 mb-6">
          <span class="flex-shrink-0"><Alert color="neutral-700" /></span>
          <div class="flex-col flex gap-2 items-start">
            <p class="text-2xl font-bold">Complete Price Adjustment</p>
            <p class="text-sm text-neutral-500 text-left">
              Are you sure you want to mark this price adjustment as "Additional
              Payment Completed"? This action cannot be undone.
            </p>
          </div>
        </div>
        <div class="flex gap-4 justify-end">
          <button
            @click="toggleConfirmModal()"
            class="px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="handleConfirmComplete()"
            class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Confirm
          </button>
        </div>
      </template>
    </Modal>

    <Modal
      :modalVisible="visibleRejectModal"
      @close="toggleRejectModal()"
      width="600px"
      id="rejection-modal"
    >
      <template slot="modal-content">
        <div class="flex items-center justify-start gap-6 mb-6">
          <span class="flex-shrink-0"><Alert color="neutral-700" /></span>
          <div class="flex-col flex gap-2 items-start">
            <p class="text-2xl font-bold">Reject Price Adjustment</p>
            <p class="text-sm text-neutral-500 text-left">
              Are you sure you want to reject this price adjustment? The status
              will be reverted to "Pending Additional Payment".
            </p>
          </div>
        </div>
        <div class="flex gap-4 justify-end">
          <button
            @click="toggleRejectModal()"
            class="px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="handleConfirmReject()"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Reject
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { formatDate, currencyFormat } from "@/util"
import "vue2-daterange-picker/dist/vue2-daterange-picker.css"
import dayjs from "dayjs"

export default {
  name: "PriceAdjustment",
  filters: {
    formatDate,
    currencyFormat
  },
  components: {
    CustomSelect: () =>
      import(/* webpackChunkName: "Forms" */ "@/components/Forms/CustomSelect"),
    TableComponent: () =>
      import(/* webpackChunkName: "Table" */ "@/components/Table/Table"),
    Pagination: () =>
      import(
        /* webpackChunkName: "Pagination" */ "@/components/Pagination/Pagination"
      ),
    Modal: () =>
      import(/* webpackChunkName: "Modal" */ "@/components/Modal/Modal"),
    Alert: () =>
      import(/* webpackChunkName: "icon" */ "@/components/Icons/Alert"),
    DateRangePicker: () => import("vue2-daterange-picker"),
    Calendar: () =>
      import(/* webpackChunkName: "Forms" */ "@/components/Icons/Calendar")
  },
  data() {
    const NOW = new Date()
    return {
      selectedStatus: "",
      dateRange: {
        startDate: new Date(
          NOW.getFullYear(),
          NOW.getMonth() - 1,
          NOW.getDate()
        ),
        endDate: NOW
      },
      selectedSort: "DESC",
      statusOptions: [
        "Pending Additional Payment",
        "Payment Under Verification",
        "Additional Payment Completed"
      ],
      sortOptions: ["ASC", "DESC"],
      columns: [
        { label: "Order ID", field: "order_id" },
        { label: "Old Details", field: "old_details" },
        { label: "New Details", field: "new_details" },
        { label: "Difference", field: "difference" },
        { label: "Status", field: "status" },
        { label: "Created At", field: "created_at" },
        { label: "Action", field: "action" }
      ],
      currentPage: 1,
      pageSize: 10,
      isLoading: false,
      visibleConfirmModal: false,
      visibleRejectModal: false,
      selectedOrderId: null
    }
  },
  mounted() {
    this.loadPriceAdjustments()
  },
  computed: {
    ...mapGetters("common", ["isMobile"]),
    ...mapGetters("priceAdjustment", [
      "priceAdjustmentList",
      "priceAdjustmentMeta"
    ]),
    totalRecords() {
      return this.priceAdjustmentMeta?.total_item || 0
    },
    start() {
      return (this.currentPage - 1) * this.pageSize + 1
    },
    end() {
      const lastOnPage = (this.currentPage - 1) * this.pageSize + this.pageSize
      return lastOnPage > this.totalRecords ? this.totalRecords : lastOnPage
    }
  },
  methods: {
    ...mapActions("priceAdjustment", [
      "getPriceAdjustments",
      "completePriceAdjustment",
      "rejectPriceAdjustment"
    ]),
    ...mapActions("common", ["showLoading", "hideLoading", "setToast"]),
    loadPriceAdjustments(page = 1) {
      this.isLoading = true
      this.showLoading()

      const params = {
        page: page,
        per_page: this.pageSize,
        sort: this.selectedSort
      }

      if (this.selectedStatus) {
        params.status = this.selectedStatus
      }

      if (this.dateRange.startDate) {
        params.start_date = dayjs(String(this.dateRange.startDate))
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss")
      }

      if (this.dateRange.endDate) {
        params.end_date = dayjs(String(this.dateRange.endDate))
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss")
      }

      this.getPriceAdjustments({ params })
        .then(() => {
          this.isLoading = false
          this.hideLoading()
        })
        .catch((error) => {
          console.error("Error loading price adjustments:", error)
          this.isLoading = false
          this.hideLoading()
        })
    },
    handleFilterChange() {
      this.currentPage = 1
      this.loadPriceAdjustments(1)
    },
    handleDateRangeChange(value) {
      this.dateRange = {
        startDate: value.startDate,
        endDate: value.endDate
      }
      this.handleFilterChange()
    },
    onPageChange(page) {
      this.currentPage = page
      this.loadPriceAdjustments(page)
    },
    getStatusClass(status) {
      switch (status) {
        case "Pending Additional Payment":
          return "bg-yellow-100 text-yellow-800"
        case "Payment Under Verification":
          return "bg-blue-100 text-blue-800"
        case "Additional Payment Completed":
          return "bg-green-100 text-green-800"
        default:
          return "bg-gray-100 text-gray-800"
      }
    },
    toggleConfirmModal() {
      this.visibleConfirmModal = !this.visibleConfirmModal
    },
    toggleRejectModal() {
      this.visibleRejectModal = !this.visibleRejectModal
      if (!this.visibleRejectModal) {
        this.selectedOrderId = null
      }
    },
    handleCompleteClick(orderId) {
      this.selectedOrderId = orderId
      this.toggleConfirmModal()
    },
    handleRejectClick(orderId) {
      this.selectedOrderId = orderId
      this.toggleRejectModal()
    },
    handleConfirmComplete() {
      if (!this.selectedOrderId) return

      this.showLoading()
      this.completePriceAdjustment({ orderId: this.selectedOrderId })
        .then((response) => {
          this.hideLoading()
          this.toggleConfirmModal()
          this.setToast({
            options: {
              type: 'success',
              duration: 3000,
              primaryText: 'Berhasil',
              secondaryText: response.data.message || "Price adjustment completed successfully"
            }
          })
          this.loadPriceAdjustments(this.currentPage)
          this.selectedOrderId = null
        })
        .catch((error) => {
          this.hideLoading()
          console.error("Error completing price adjustment:", error)
          this.setToast({
            options: {
              type: 'error',
              duration: 3000,
              primaryText: 'Gagal',
              secondaryText: error.response?.data?.message || "Failed to complete price adjustment"
            }
          })
        })
    },
    handleConfirmReject() {
      if (!this.selectedOrderId) return

      this.showLoading()
      this.rejectPriceAdjustment({ orderId: this.selectedOrderId })
        .then((response) => {
          this.hideLoading()
          this.toggleRejectModal()
          this.setToast({
            options: {
              type: 'success',
              duration: 3000,
              primaryText: 'Berhasil',
              secondaryText: response.data.message || "Price adjustment rejected successfully"
            }
          })
          this.loadPriceAdjustments(this.currentPage)
        })
        .catch((error) => {
          this.hideLoading()
          console.error("Error rejecting price adjustment:", error)
          this.setToast({
            options: {
              type: 'error',
              duration: 3000,
              primaryText: 'Gagal',
              secondaryText: error.response?.data?.message || "Failed to reject price adjustment"
            }
          })
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.price-adjustment {
  padding: 0;
}
.date-range {
  &::v-deep .reportrange-text {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    padding-left: 2.75rem;
    padding-right: 0.75rem;
    border-radius: 0.5rem;
    border-color: rgba(206, 213, 222, 1);
    border-width: 1px;
    border-style: solid;
    width: 100%;
    height: 50px;
    display: flex;
    align-items: center;
  }
  &::v-deep .vue-daterange-picker {
    display: block;
  }
}
</style>
